import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { FiCheckSquare, FiSquare } from 'react-icons/fi';

export default function FiscalChecklist({ modoOscuro }) {
  const { secureFetch } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureFetch('/api/fiscal/checklist');
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (id, done) => {
    if (!data) return;
    const manualIds = (data.items || [])
      .filter((i) => i.manualDone && i.id !== id)
      .map((i) => i.id);
    const nextIds = done ? [...manualIds, id] : manualIds;
    setSaving(true);
    try {
      await secureFetch('/api/fiscal/checklist', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mes: data.mes, doneIds: nextIds }),
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className={`text-xs ${textMuted}`}>Cargando checklist del mes…</p>;
  }
  if (!data?.items?.length) return null;

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${box}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className={`text-sm font-semibold ${textPri}`}>Checklist fiscal del mes</h3>
          <p className={`text-xs ${textMuted}`}>
            {data.completed}/{data.total} completados · {data.mes}
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {data.items.map((item) => (
          <li key={item.id} className="flex items-start gap-2">
            <button
              type="button"
              disabled={saving || item.autoDone}
              onClick={() => toggle(item.id, !item.done)}
              className={`mt-0.5 shrink-0 ${item.autoDone ? 'opacity-60 cursor-default' : ''}`}
              title={item.autoDone ? 'Completado automáticamente' : 'Marcar manualmente'}
            >
              {item.done ? (
                <FiCheckSquare className="w-4 h-4 text-[#1F80FF]" />
              ) : (
                <FiSquare className={`w-4 h-4 ${textMuted}`} />
              )}
            </button>
            <div className="min-w-0 flex-1">
              <div className={`text-sm ${item.done ? textMuted : textPri}`}>{item.label}</div>
              {item.hint && <div className={`text-[11px] ${textMuted}`}>{item.hint}</div>}
              {item.href && !item.done && (
                <Link to={item.href} className="text-[11px] font-semibold text-[#1F80FF] hover:underline">
                  Ir →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
